<script>
import BaseView from "@/mixins/BaseView.vue";
import { useFiltersCache } from "@/composables";
import OmniTable from "@/components/omni/OmniTable.vue";
import PageHeader from "@/components/header/PageHeader.vue";
import TableFilter from "@/components/tables/TableFilter.vue";
import TableFilterRow from "@/components/tables/TableFilterRow.vue";
import SearchField from "@/components/inputs/SearchField.vue";
import ValidationMixin from "@/mixins/validation.mixin.ts";
import ArchiveDispatcherAction from "@/views/dispatchers/components/ArchiveDispatcherAction.vue";
import DispatcherRemoveDialog from "@/views/dispatchers/components/DispatcherRemoveDialog.vue";
import { mapActions, mapState } from "pinia";
import { useAppStore } from "@/store/app.store.ts";
import { useSnackbarStore } from "@/store/snackbar.store.ts";
import LinkButton from "@/components/buttons/LinkButton.vue";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto.ts";

const { setSearchParams } = useFiltersCache();

export default {
  name: "DispatchersPage",
  components: {
    PermissionTooltip,
    LinkButton,
    DispatcherRemoveDialog,
    ArchiveDispatcherAction,
    SearchField,
    TableFilterRow,
    TableFilter,
    PageHeader,
    OmniTable,
  },
  mixins: [BaseView, ValidationMixin],
  data() {
    return {
      perm: "dispatchers",
      headers: [
        { title: this.$t("general.name"), key: "name" },
        { title: this.$t("dispatchers.roles"), key: "roles" },
        { title: this.$t("general.email"), key: "email" },
        { title: this.$t("general.phone"), key: "phone" },
        { title: this.$t("dispatchers.phone-ext"), key: "phoneExt" },
        {
          title: "Actions",
          key: "actions",
          align: "center",
          width: 96,
          sortable: false,
        },
      ],
      editDialog: false,
      removeDialog: false,
      filter: { search: "" },
      data: { total: 0 },
      selectedDispatcher: {},
      loading: false,
      archived: false,
    };
  },
  computed: {
    DispatcherPermissionValue() {
      return DispatcherPermissionValue;
    },
    ...mapState(useAppStore, ["mobile", "dispatchers"]),
    rolesMap() {
      return this.arrayToMap(this.roles);
    },
  },
  watch: {
    archived() {
      this.refresh();
    },
  },
  created() {
    this.setTitle("Dispatchers");
    this.refresh();
  },
  methods: {
    ...mapActions(useSnackbarStore, ["showSnackbarError", "showSnackbarSuccess"]),
    async refresh() {
      const params = { ...this.filter, ...this.params };
      params.archived = this.archived;

      this.loading = true;
      try {
        const resp = await this.$api.dispatchers.findDispatchers(params);
        this.data = resp.data;
      } catch (error) {
        console.error("Error load dispatchers list: ", error);
      }
      this.loading = false;
    },
    getRoles(roles) {
      return roles.map((r) => this.rolesMap[r]).join(", ");
    },
    showRemoveConfirmation(item) {
      this.selectedDispatcher = item;
      this.removeDialog = true;
    },
    search() {
      setSearchParams("dispatchers", this.filter);
      this.refresh();
    },
    clearSearch() {
      this.filter = {};
      this.search();
    },
    printName(item) {
      return item.name || `${item.firstName} ${item.lastName}`;
    },
  },
};
</script>

<template>
  <v-container :fluid="true" class="pt-6 pb-10 pl-6 pr-6 height-100">
    <page-header header="Dispatchers">
      <template #suffix>
        <permission-tooltip :permission="DispatcherPermissionValue.EditOrCreateDispatcher">
          <template #activator="{ disabled }">
            <link-button
              :disabled="disabled"
              :to="{
                name: 'dispatcher-create',
              }"
              color="success"
            >
              {{ $t("general.create-new") }}
            </link-button>
          </template>

          {{ $t("permission.create-dispatcher") }}
        </permission-tooltip>
      </template>
    </page-header>
    <table-filter :expandable="false" @apply="search" @clear="clearSearch">
      <template #header>
        <table-filter-row :divider="false" :label="$t('general.filter-main')" icon="mdi-filter-variant">
          <v-col cols="6" sm="2">
            <search-field v-model="filter.name" label="Name" @search="search" />
          </v-col>
          <v-col cols="6" sm="2">
            <search-field v-model="filter.email" label="Email" @search="search" />
          </v-col>
        </table-filter-row>
      </template>
    </table-filter>

    <omni-table
      v-model:archived="archived"
      :footer-props="{ 'items-per-page-options': pagination.itemsPerPage }"
      :headers="headers"
      :items="data.items"
      :items-length="data.total"
      :items-per-page="params.pageSize"
      :loading="loading"
      :mobile-breakpoint="0"
      :page="params.page"
      class="dispatchers-table"
      show-archive
      @update:page="updatePage"
      @update:items-per-page="updatePageSize"
    >
      <template #[`item.name`]="{ item }"> {{ printName(item) }}</template>
      <template #[`item.email`]="{ item }">
        <span :class="{ 'text-error': !item.emailConfirmed }">{{ item.email }}</span>
      </template>
      <template #[`item.roles`]="{ item }">
        {{ getRoles(item.roles) }}
      </template>
      <template #[`item.actions`]="{ item }">
        <div class="d-flex justify-space-evenly">
          <permission-tooltip :permission="DispatcherPermissionValue.EditOrCreateDispatcher">
            <template #activator="{ disabled }">
              <link-button
                :disabled="disabled"
                :to="{
                  name: 'dispatcher-edit',
                  params: { id: item.id },
                }"
                class="pa-0 table-action"
                variant="text"
              >
                <v-icon size="small">mdi-pencil-outline</v-icon>
              </link-button>
            </template>

            {{ $t("permission.edit-dispatcher") }}
          </permission-tooltip>

          <archive-dispatcher-action
            v-if="item.emailConfirmed"
            :dispatcher="item"
            @change="refresh"
          ></archive-dispatcher-action>

          <permission-tooltip v-else :permission="DispatcherPermissionValue.EditOrCreateDispatcher">
            <template #activator="{ disabled }">
              <v-btn
                :disabled="disabled"
                color="primary"
                icon="mdi-delete-outline"
                size="x-small"
                variant="text"
                @click="showRemoveConfirmation(item)"
              ></v-btn>
            </template>

            {{ $t("permission.remove-dispatcher") }}
          </permission-tooltip>
        </div>
      </template>
    </omni-table>
    <dispatcher-remove-dialog
      v-model="removeDialog"
      :dispatcher="selectedDispatcher"
      @remove="refresh"
    ></dispatcher-remove-dialog>
  </v-container>
</template>
